import React, { Suspense, useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { getSharedCommentAgentSessions } from '@confluence/agent-mention/entry-points/sharedCommentAgentSessions.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { LoadableLazy } from '@confluence/loadable';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { ContentCommentAgentSessionService } from './content-comment-agent-session-service.ts';
import type { CommentAgentReplyTrackerProps } from './ContentCommentAgentReplyTracker.tsx';

const ReplyTracker = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentCommentAgentReplyTracker" */ './ContentCommentAgentReplyTracker.tsx'
			)
		).ContentCommentAgentReplyTracker,
	loading: ({ error }) => {
		if (error) throw error;
		return null;
	},
});
const renderNothing = () => null;

function ReplyObservation({
	associated,
	sessionState,
	...props
}: CommentAgentReplyTrackerProps & { associated: boolean; sessionState?: string | null }) {
	const eligible = sessionState === 'INPUT_REQUIRED' || sessionState === 'ARTEFACT_READY';
	// Keep the approval controller mounted if a ready session resumes work.
	const [hasStartedReplyLookup, setHasStartedReplyLookup] = useState(false);
	useEffect(() => {
		if (eligible) setHasStartedReplyLookup(true);
	}, [eligible]);
	const active = associated && (eligible || hasStartedReplyLookup);
	const { registry, request } = props;
	useEffect(() => {
		if (!active)
			registry.publish(request, {
				status:
					associated && (sessionState === 'WORKING' || sessionState === 'THINKING')
						? 'loading'
						: 'unavailable',
			});
	}, [active, associated, sessionState, registry, request]);
	const onError = useCallback(
		() => registry.publish(request, { status: 'unavailable' }),
		[registry, request],
	);
	return active ? (
		<GenericErrorBoundary
			attribution={Attribution.COMMENTS}
			renderOnError={renderNothing}
			onError={onError}
		>
			<Suspense fallback={null}>
				<ReplyTracker {...props} sessionState={sessionState} />
			</Suspense>
		</GenericErrorBoundary>
	) : null;
}

export function ContentCommentAgentReplies({
	contentId,
	registry,
}: {
	contentId: string;
	registry: ContentCommentAgentSessionService;
}): React.JSX.Element {
	const environment = useRelayEnvironment();
	const { cloudId, userId } = useSessionData();
	const store = getSharedCommentAgentSessions(environment, cloudId ?? '', userId ?? '', contentId);
	const sessions = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
	const requests = useSyncExternalStore(
		registry.replies.subscribeToRequests,
		registry.replies.getRequests,
		registry.replies.getRequests,
	);
	const { commentService } = useCommentService();
	const onActionAccepted = useCallback(async () => {
		if (registry.replies.isDisposed()) return;
		// Refresh the Confluence side panel (or queue it); Canvas refreshes its own thread.
		await commentService.triggerCommentPanelRefresh();
	}, [commentService, registry]);
	return (
		<>
			{requests.map((request) => {
				const source = sessions.sessionMap.get(request.commentId);
				const session = sessions.states.get(request.commentId)?.session ?? source;
				return (
					<ReplyObservation
						key={request.id}
						contentId={contentId}
						request={request}
						registry={registry.replies}
						associated={sessions.conversationMap.get(request.commentId) === request.conversationId}
						sessionState={session?.state}
						agentId={source?.agentId}
						onActionAccepted={onActionAccepted}
					/>
				);
			})}
		</>
	);
}
